import { supabase } from "@/integrations/supabase/client";

export interface Holiday {
  id: string;
  name: string;
  date: string;
  company_id: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Service for managing holiday operations
 * Follows best practices for performance and caching
 */
export class HolidayService {
  // Cache for holidays with expiration
  private static holidayCache = new Map<string, { 
    data: Holiday[]; 
    timestamp: number; 
  }>();
  private static readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

  /**
   * Fetch holidays for a company within a date range
   * Uses caching for performance optimization
   */
  static async fetchHolidays(
    companyId: string,
    startDate: string,
    endDate: string
  ): Promise<Holiday[]> {
    const cacheKey = `${companyId}-${startDate}-${endDate}`;
    const now = Date.now();

    // Check cache first
    const cached = this.holidayCache.get(cacheKey);
    if (cached && (now - cached.timestamp) < this.CACHE_DURATION) {
      return cached.data;
    }

    try {
      const { data, error } = await supabase
        .from('holidays')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_public', true)
        .gte('date', startDate)
        .lte('date', endDate)
        .order('date');

      if (error) {
        throw new Error(`Failed to fetch holidays: ${error.message}`);
      }

      const holidays = data || [];

      // Cache the result
      this.holidayCache.set(cacheKey, {
        data: holidays,
        timestamp: now
      });

      // Clean up expired cache entries occasionally
      this.cleanupCache();

      return holidays;
    } catch (error) {
      console.error('Error fetching holidays:', error);
      throw error;
    }
  }

  /**
   * Fetch holidays for a specific month
   * @param month - Month number (1-12, where 1 = January, 12 = December)
   */
  static async fetchHolidaysForMonth(
    companyId: string,
    year: number,
    month: number
  ): Promise<Holiday[]> {
    const startDate = `${year}-${month.toString().padStart(2, '0')}-01`;
    // Get last day of current month using Date constructor
    // Date constructor uses 0-indexed months, so new Date(year, month, 0) gives last day of (month-1) in 0-indexed terms
    // which is the last day of our target month since we're passing 1-indexed month
    const lastDay = new Date(year, month, 0).getDate();
    const endDate = `${year}-${month.toString().padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`;
    
    console.log(`Fetching holidays for ${year}-${month}: ${startDate} to ${endDate}`);
    
    return this.fetchHolidays(companyId, startDate, endDate);
  }

  /**
   * Check if a specific date is a holiday
   */
  static async isHoliday(
    companyId: string,
    date: string
  ): Promise<Holiday | null> {
    try {
      const { data, error } = await supabase
        .from('holidays')
        .select('*')
        .eq('company_id', companyId)
        .eq('date', date)
        .eq('is_public', true)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // No holiday found
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error checking holiday:', error);
      return null;
    }
  }

  /**
   * Get holidays for a specific year (for calendar display)
   */
  static async fetchHolidaysForYear(
    companyId: string,
    year: number
  ): Promise<Holiday[]> {
    const startDate = `${year}-01-01`;
    const endDate = `${year}-12-31`;
    
    return this.fetchHolidays(companyId, startDate, endDate);
  }

  /**
   * Clean up expired cache entries
   */
  private static cleanupCache(): void {
    const now = Date.now();
    const expiredKeys = Array.from(this.holidayCache.entries())
      .filter(([, data]) => (now - data.timestamp) >= this.CACHE_DURATION)
      .map(([key]) => key);
    
    expiredKeys.forEach(key => this.holidayCache.delete(key));
  }

  /**
   * Clear all cache (useful for testing or when holidays are updated)
   */
  static clearCache(): void {
    this.holidayCache.clear();
  }
}
